import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera } from 'lucide-react';
import type { MeasurementSessionSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useAcceptMeasurementSessionMutation,
  useRecordMeasurementMutation,
  useRetakeMeasurementSessionMutation,
} from '../api/measurements.api';

/** Millimetres as the API takes them: up to three digits and at most one decimal place. */
const MILLIMETRES = /^\d{1,3}(\.\d)?$/;

/** Tenths, so `31.7 + 32.6` is 64.3 rather than what binary makes of it. */
const tenths = (value: string): number | null => {
  const match = MILLIMETRES.exec(value.trim());
  return match ? Math.round(Number(match[0]) * 10) : null;
};

const millimetres = (t: number): string =>
  `${t < 0 ? '-' : ''}${Math.floor(Math.abs(t) / 10)}.${Math.abs(t) % 10}`;

/**
 * The desk reviewing a camera PD estimate.
 *
 * The estimate is shown as what it is — a reference figure from an assumed iris diameter — beside
 * the PD the technician measured their own way, with the difference between them. The technician's
 * figure is the one that is saved as authoritative; the estimate is kept in the history for
 * comparison and can never be copied onto a prescription.
 */
export function CameraPdReview({
  session,
  customerId,
  onDone,
}: {
  session: MeasurementSessionSummary;
  customerId: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const accept = useAcceptMeasurementSessionMutation();
  const retake = useRetakeMeasurementSessionMutation();
  const record = useRecordMeasurementMutation(customerId);

  const [technicianPd, setTechnicianPd] = useState('');
  const [error, setError] = useState<string | null>(null);

  const estimate = session.candidate?.totalPdMm ?? null;
  const typed = tenths(technicianPd);
  const estimateTenths = estimate === null ? null : tenths(estimate);
  const difference = typed !== null && estimateTenths !== null ? typed - estimateTenths : null;

  const busy = accept.isPending || record.isPending || retake.isPending;

  /**
   * The technician's own figure becomes the measurement; the estimate is filed beside it.
   *
   * Their figure is saved first. If the estimate cannot be filed afterwards the customer still has
   * the authoritative number, which is the one a lens is ground to.
   */
  const confirm = () => {
    if (typed === null) return;
    setError(null);
    record.mutate(
      { totalPdMm: millimetres(typed), pdType: 'DISTANCE' },
      {
        onSuccess: () =>
          accept.mutate(
            { sessionId: session.id },
            {
              onSuccess: onDone,
              onError: (caught) =>
                setError(apiErrorMessage(caught) ?? t('measurements.camera.errors.accept')),
            },
          ),
        onError: (caught) => setError(apiErrorMessage(caught) ?? t('measurements.errors.record')),
      },
    );
  };

  return (
    <div className="space-y-4">
      {error ? <Alert tone="error">{error}</Alert> : null}

      <Alert tone="warning">{t('measurements.camera.referenceOnly')}</Alert>

      <dl className="border-line divide-line divide-y rounded-lg border">
        <div className="flex items-center justify-between gap-4 p-3">
          <dt className="text-muted flex items-center gap-2 text-sm">
            <Camera className="size-4" aria-hidden />
            {t('measurements.camera.estimate')}
          </dt>
          <dd className="text-ink text-lg font-semibold tabular-nums">
            {estimate === null ? '—' : `${estimate} mm`}
          </dd>
        </div>

        <div className="flex items-center justify-between gap-4 p-3">
          <dt className="text-muted text-sm">
            <label htmlFor="technician-pd">{t('measurements.camera.technician')}</label>
          </dt>
          <dd className="w-32">
            <Input
              id="technician-pd"
              inputMode="decimal"
              autoComplete="off"
              value={technicianPd}
              onChange={(event) => setTechnicianPd(event.target.value)}
              placeholder={t('measurements.fields.totalPd')}
              aria-describedby="technician-pd-hint"
            />
          </dd>
        </div>

        <div className="flex items-center justify-between gap-4 p-3">
          <dt className="text-muted text-sm">{t('measurements.camera.difference')}</dt>
          <dd
            className="text-ink text-sm font-medium tabular-nums"
            role="status"
            aria-live="polite"
          >
            {difference === null
              ? '—'
              : `${difference > 0 ? '+' : ''}${millimetres(difference)} mm`}
          </dd>
        </div>
      </dl>

      <p id="technician-pd-hint" className="text-muted text-xs">
        {t('measurements.camera.technicianHint')}
      </p>

      <div className="flex flex-wrap gap-2">
        <Button onClick={confirm} isLoading={busy} disabled={typed === null}>
          {t('measurements.camera.confirm')}
        </Button>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setError(null);
            retake.mutate(session.id, {
              onError: (caught) =>
                setError(apiErrorMessage(caught) ?? t('measurements.camera.errors.retake')),
            });
          }}
        >
          {t('measurements.camera.retake')}
        </Button>
      </div>
    </div>
  );
}
